import { oc } from "@orpc/contract";
import { z } from "zod";

const backfillRunStatusEnum = z.enum([
  "pending",
  "running",
  "completed",
  "failed",
  "cancelled",
  "interrupted",
]);

const backfillRunSchema = z.object({
  id: z.string(),
  backfillName: z.string(),
  status: backfillRunStatusEnum,
  orgId: z.string().nullable(),
  dryRun: z.boolean(),
  limit: z.number().nullable(),
  chunkSize: z.number().nullable(),
  processed: z.number(),
  total: z.number().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  error: z.string().nullable(),
  triggeredByUserId: z.string().nullable(),
  triggeredBySource: z.enum(["user", "control-plane"]),
  triggeredByLabel: z.string().nullable(),
  createdAt: z.string(),
});

export const backfillsContracts = {
  listBackfills: oc
    .input(z.object({}))
    .output(
      z.object({
        executionTargets: z.array(
          z.object({
            target: z.enum(["worker", "render"]),
            available: z.boolean(),
            missingRequirements: z.array(z.string()),
          }),
        ),
        items: z.array(
          z.object({
            name: z.string(),
            description: z.string(),
            createdAt: z.string().nullable(),
            labels: z.array(z.string()),
            chunkSize: z.number().nullable(),
            chunkTimeout: z.number().nullable(),
            params: z.array(
              z.object({
                key: z.string(),
                required: z.boolean(),
                default: z.string().nullable(),
                description: z.string().nullable(),
              }),
            ),
            lastRun: backfillRunSchema.nullable(),
          }),
        ),
      }),
    )
    .route({
      summary: "List available data backfills",
      description:
        "Admin only. Lists registered backfill jobs with their parameters, labels and most recent run, plus which execution targets are currently available.",
      method: "POST",
    }),

  triggerBackfill: oc
    .input(
      z.object({
        backfillName: z.string(),
        executionTarget: z.enum(["worker", "render"]),
        orgId: z.string().optional(),
        dryRun: z.boolean(),
        limit: z.number().int().positive().optional(),
        /** Free-form key/value params passed through to the backfill handler. */
        params: z.record(z.string(), z.string()).optional(),
      }),
    )
    .output(
      z.object({
        runId: z.string(),
      }),
    )
    .route({
      summary: "Start a data backfill run",
      description:
        "Admin only. Queues a backfill run on a worker or one-off job, optionally scoped to an organization, as a dry run, with a row limit and custom parameters.",
      method: "POST",
    }),

  listBackfillRuns: oc
    .input(
      z.object({
        backfillName: z.string().optional(),
        status: backfillRunStatusEnum.optional(),
        page: z.number().int().positive().default(1),
        pageSize: z.number().int().positive().max(100).default(20),
      }),
    )
    .output(
      z.object({
        items: z.array(backfillRunSchema),
        total: z.number(),
      }),
    )
    .route({
      summary: "List backfill run history",
      description:
        "Admin only. Paged backfill runs, filterable by backfill name and status, with progress counts, timing, errors and who triggered each run.",
      method: "POST",
    }),

  getBackfillRun: oc
    .input(
      z.object({
        runId: z.string(),
      }),
    )
    .output(backfillRunSchema)
    .route({
      summary: "Get a backfill run",
      description:
        "Admin only. Returns one backfill run's status, progress, timing, error and trigger details by run id.",
      method: "POST",
    }),

  estimateBackfill: oc
    .input(
      z.object({
        backfillName: z.string(),
        orgId: z.string().optional(),
        /** Free-form key/value params (same as trigger) — e.g. invoiceId for scoped estimates. */
        params: z.record(z.string(), z.string()).optional(),
      }),
    )
    .output(
      z.object({
        total: z.number().nullable(),
      }),
    )
    .route({
      summary: "Estimate rows a backfill would process",
      description:
        "Admin only. Counts how many records a backfill would touch for an optional organization and parameters; returns null when the estimate is unknown or times out.",
      method: "POST",
    }),

  markRunFailed: oc
    .input(
      z.object({
        runId: z.string(),
      }),
    )
    .output(
      z.object({
        success: z.boolean(),
      }),
    )
    .route({
      summary: "Mark a stuck backfill run as failed",
      description:
        "Admin only. Sets a pending or running backfill run to failed, for runs that are stuck; rejects runs already finished.",
      method: "POST",
    }),
};
